<template>
    <div class="step-preview">
        <div class="preview-container">
            <div class="preview-header">
                <span class="preview-header__brand">{{ $t('surveys.wizard.preview.brand') }}</span>
            </div>

            <div class="preview-body">
                <h2 class="preview-body__title">{{ survey.title || $t('surveys.wizard.preview.noTitle') }}</h2>
                <p class="preview-body__description" v-if="survey.description">
                    {{ survey.description }}
                </p>

                <div class="preview-body__questions">
                    <div
                        v-for="(question, index) in survey.questions"
                        :key="index"
                        class="preview-question"
                    >
                        <div class="preview-question__text">
                            {{ index + 1 }}. {{ question.title || $t('surveys.wizard.preview.emptyQuestion') }}
                            <span v-if="question.required" class="preview-question__required">*</span>
                        </div>

                        <!-- Texto corto -->
                        <v-text-field
                            v-if="question.type === 'SHORT_TEXT'"
                            :placeholder="$t('surveys.wizard.preview.shortTextPlaceholder')"
                            outlined
                            dense
                            disabled
                            class="preview-input"
                        ></v-text-field>

                        <!-- Texto largo -->
                        <v-textarea
                            v-if="question.type === 'LONG_TEXT'"
                            :placeholder="$t('surveys.wizard.preview.longTextPlaceholder')"
                            outlined
                            dense
                            rows="3"
                            disabled
                            class="preview-input"
                        ></v-textarea>

                        <!-- Verdadero/Falso -->
                        <v-radio-group
                            v-if="question.type === 'TRUE_FALSE'"
                            disabled
                            class="preview-radio-group"
                        >
                            <v-radio :label="$t('surveys.wizard.preview.yes')" value="true"></v-radio>
                            <v-radio :label="$t('surveys.wizard.preview.no')" value="false"></v-radio>
                        </v-radio-group>

                        <!-- Seleccion unica -->
                        <v-radio-group
                            v-if="question.type === 'SINGLE_CHOICE'"
                            disabled
                            class="preview-radio-group"
                        >
                            <v-radio
                                v-for="(option, optIndex) in question.options"
                                :key="optIndex"
                                :label="option.label || $t('surveys.wizard.preview.option') + ' ' + (optIndex + 1)"
                                :value="option.label"
                            ></v-radio>
                            <v-radio
                                v-if="question.config && question.config.allowOther"
                                :label="$t('surveys.wizard.preview.other')"
                                value="other"
                            ></v-radio>
                        </v-radio-group>

                        <!-- Lista desplegable -->
                        <v-select
                            v-if="question.type === 'DROPDOWN'"
                            :items="[...(question.options || []).map(o => o.label || ''), ...(question.config && question.config.allowOther ? [$t('surveys.wizard.preview.other')] : [])]"
                            :placeholder="$t('surveys.wizard.preview.selectPlaceholder')"
                            outlined
                            dense
                            class="preview-input"
                        ></v-select>

                        <!-- Seleccion multiple -->
                        <div v-if="question.type === 'MULTI_CHOICE'" class="preview-checkbox-group">
                            <v-checkbox
                                v-for="(option, optIndex) in question.options"
                                :key="optIndex"
                                :label="option.label || $t('surveys.wizard.preview.option') + ' ' + (optIndex + 1)"
                                disabled
                                dense
                                hide-details
                            ></v-checkbox>
                            <v-checkbox
                                v-if="question.config && question.config.allowOther"
                                :label="$t('surveys.wizard.preview.other')"
                                disabled
                                dense
                                hide-details
                            ></v-checkbox>
                        </div>

                        <!-- Calificacion -->
                        <div v-if="question.type === 'RATING'" class="preview-rating">
                            <v-rating
                                :length="question.config.scale || 5"
                                :empty-icon="getRatingIcon(question.config.icon)"
                                :full-icon="getRatingIcon(question.config.icon)"
                                readonly
                                color="amber"
                                background-color="grey lighten-2"
                            ></v-rating>
                        </div>

                        <!-- Escala lineal -->
                        <div v-if="question.type === 'LINEAR_SCALE'" class="preview-scale">
                            <v-slider
                                :min="question.config.min || 0"
                                :max="question.config.max || 5"
                                thumb-label
                                ticks
                                disabled
                            ></v-slider>
                            <div class="preview-scale__labels">
                                <span>{{ question.config.minLabel || question.config.min || 0 }}</span>
                                <span>{{ question.config.maxLabel || question.config.max || 5 }}</span>
                            </div>
                        </div>

                        <!-- Numero -->
                        <v-text-field
                            v-if="question.type === 'NUMBER'"
                            type="number"
                            :placeholder="$t('surveys.wizard.preview.numberPlaceholder')"
                            outlined
                            dense
                            disabled
                            class="preview-input"
                        ></v-text-field>

                        <!-- Fecha -->
                        <v-text-field
                            v-if="question.type === 'DATE'"
                            type="date"
                            :placeholder="$t('surveys.wizard.preview.datePlaceholder')"
                            outlined
                            dense
                            disabled
                            class="preview-input"
                        ></v-text-field>

                        <!-- NPS -->
                        <div v-if="question.type === 'NPS'" class="preview-scale">
                            <v-slider
                                :min="0"
                                :max="10"
                                step="1"
                                ticks="always"
                                tick-size="4"
                                thumb-label
                                disabled
                            ></v-slider>
                            <div class="preview-scale__labels">
                                <span>0 - {{ $t('surveys.wizard.preview.npsUnlikely') }}</span>
                                <span>10 - {{ $t('surveys.wizard.preview.npsLikely') }}</span>
                            </div>
                        </div>
                    </div>
                </div>

            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: "StepPreview",
    props: {
        survey: {
            type: Object,
            required: true
        }
    },
    methods: {
        getRatingIcon(icon) {
            const icons = {
                'star': 'mdi-star',
                'heart': 'mdi-heart',
                'thumb': 'mdi-thumb-up'
            };
            return icons[icon] || 'mdi-star';
        }
    }
};
</script>

<style lang="css" scoped>
    .step-preview {
        display: flex;
        justify-content: center;
    }

    .preview-container {
        max-width: 100%;
        width: 100%;
        background: white;
        border: 1px solid #ECEAF2;
        border-radius: 16px;
        overflow: hidden;
    }

    .preview-header {
        height: 64px;
        background: linear-gradient(90deg, #4F2BB8, #6E17A5);
        padding: 20px 24px;
        display: flex;
        align-items: center;
    }

    .preview-header__brand {
        color: white;
        font-weight: 700;
        font-size: 16px;
    }

    .preview-body {
        padding: 30px 40px;
    }

    .preview-body__title {
        font-size: 22px;
        color: var(--v-primary-base);
        font-weight: 700;
        margin-bottom: 8px;
    }

    .preview-body__description {
        font-size: 13px;
        color: #6B6B76;
        margin-bottom: 30px;
        line-height: 1.5;
    }

    .preview-body__questions {
        display: flex;
        flex-direction: column;
        gap: 24px;
    }

    .preview-question__text {
        font-size: 14px;
        font-weight: 600;
        color: #1F1F2B;
        margin-bottom: 8px;
    }

    .preview-question__required {
        color: #E0473C;
    }

    .preview-input {
        margin-top: 4px;
    }

    .preview-radio-group {
        padding-left: 4px;
        margin-top: 4px;
    }

    .preview-checkbox-group {
        padding-left: 4px;
        margin-top: 4px;
    }

    .preview-rating {
        margin-top: 8px;
    }

    .preview-scale {
        padding: 0 8px;
        margin-top: 8px;
    }

    .preview-scale__labels {
        display: flex;
        justify-content: space-between;
        font-size: 12px;
        color: #6B6B76;
        margin-top: 4px;
    }
</style>
